import { useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import { captureEvent } from '@/services/analytics';
import { Typography } from '@/shared/ui';

import Markdown from '@/features/_shared/Markdown';
import { Dialog } from '@/shared/ui/overlays/Modal';

import { feedArticleOptions } from './feedArticle.options';

import { clock } from '@/services/clock';
import { FeedArticleDialogSkeleton } from './FeedArticleDialogSkeleton';

type FeedArticleDialogProps = {
  open: boolean;
  CTAValue: string;
  onClose: () => void;
};

export const FeedArticleDialog = ({ open, CTAValue, onClose }: FeedArticleDialogProps) => {
  const [groupName, fileName] = CTAValue.split('/');
  const contentReadyAt = useRef<number | null>(null);

  const { data, isLoading, error } = useQuery(feedArticleOptions.file(groupName, fileName, open));

  useEffect(() => {
    if (open && !isLoading && !error && data) {
      contentReadyAt.current = clock.now();
    }
  }, [open, isLoading, error, data]);

  const handleClose = () => {
    if (contentReadyAt.current !== null) {
      captureEvent('feed_article_reading_time', {
        file_name: fileName,
        group_name: groupName,
        reading_time_seconds: Math.round((clock.now() - contentReadyAt.current) / 1000)
      });
      contentReadyAt.current = null;
    }
    onClose();
  };

  return (
    <Dialog
      closeIcon
      open={open}
      sx={{ minWidth: 800 }}
      onClose={handleClose}
    >
      {isLoading && <FeedArticleDialogSkeleton />}
      {error && <Typography color='error'>"error"</Typography>}
      {!isLoading && !error && data && (
        <Typography
          component='div'
          sx={{
            overflowWrap: 'break-word',
            hyphens: 'auto',
            fontSize: '14px',
            marginTop: '-38px'
          }}
        >
          <Markdown>{data}</Markdown>
        </Typography>
      )}
    </Dialog>
  );
};
